import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { StoreLoader, Unsubscriber } from '@core-lib/classes';
import { StatutOperation } from '@core-lib/enums';
import { IConfirmModalConfig, ICoordonneeBancaire, IDevise } from '@core-lib/models';
import { ICompteForUpdate } from '@core-lib/models/compte.model';
import { DocumentMetadata, UploadDocumentModalComponent } from '@core-lib/modules';
import { DocumentGedApiService } from '@core-lib/modules/document-ged/services/document-ged-api.service';
import { CompteBancaireService } from '@core-lib/services';
import { CommonAlfrescoService } from '@core-lib/services/common-alfresco.service';
import { ConfirmationService } from '@core-lib/services/confirmation-service.service';
import { AlfrescoDocumentStoreActions, AlfrescoDocumentStoreSelectors } from '@core-lib/store/alfresco-documents-store';
import { selectDeviseReference } from '@core-lib/store/shared-store/selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ModalRef, ModalService } from '@core-lib/modules/modal-container';
import { Observable, Subject, from, of } from 'rxjs';
import { concatMap, map, switchMap, toArray } from 'rxjs/operators';
import { CoordonneesBancairesComponent } from '../coordonnees-bancaires/coordonnees-bancaires.component';
import { HistoryModalComponent } from '../history-modal/history-modal.component';



@Component({
    selector: 'rib-compte-pro',
    templateUrl: './rib-compte-pro.component.html',
    styles: [],
    standalone: false
})
export class RibCompteProComponent extends Unsubscriber implements OnInit {
	@Input() deviseCompte: IDevise;
	@Input() structure: string;
	@Input() coordonneeBancaire: ICoordonneeBancaire;
	@Input() fromBO: boolean = false;
	@Input() compteId: string;

	@Output() reloadCurrentComptes: EventEmitter<any> = new EventEmitter();

	modalRef: ModalRef;
	deviseReferenceCompte: IDevise;
	coordonneesBancairesFiles: any[] = [];
	alfrescoDocuments: DocumentMetadata[] = [];

	loader: StoreLoader = new StoreLoader(of(false), of(null));
	loaderSubject = new Subject<StoreLoader>();
	loader$ = this.loaderSubject.asObservable();

	alfrescoDocuments$ = this.store$.select(AlfrescoDocumentStoreSelectors.selectCoordonnesBancairesDocs).subscribe(res => {
		this.alfrescoDocuments = res;
	});
	alfrescoDocumentsLoader = new StoreLoader(
		this.store$.select(AlfrescoDocumentStoreSelectors.selectCoordonnesBancairesDocsIsLoading),
		this.store$.select(AlfrescoDocumentStoreSelectors.selectCoordonnesBancairesDocsError)
	);

	constructor(
		private modalService: ModalService,
		protected store$: Store,
		private confirmationService: ConfirmationService,
		private translateService: TranslateService,
		private compteBancaireService: CompteBancaireService,
		private commonAlfrescoService: CommonAlfrescoService,
		private documentGedApiService: DocumentGedApiService
	) {
		super();
		this.loader$.subscribe(res => (this.loader = res));
	}

	ngOnInit(): void {
		this.store$.select(selectDeviseReference).subscribe(res => (this.deviseReferenceCompte = res));
		if (this.coordonneeBancaire != null) {
			this.store$.dispatch(
				AlfrescoDocumentStoreActions.LoadCoordonneesBancairesDocsRequest(null, this.coordonneeBancaire.id, this.compteId)
			);
		}
	}

	openCBModal() {
		this.modalRef = this.modalService.show(CoordonneesBancairesComponent, {
			ignoreBackdropClick: true,
			class: 'modal-xl'
		});
		this.modalRef.content.isConsultMode = this.fromBO;
		this.modalRef.content.deviseReferenceCompte = this.deviseReferenceCompte?.id; // La devise de la carpa
		this.modalRef.content.deviseReference = this.deviseCompte?.id; // La devise réference du compte de reference (devise lié au compte bancaire du compte de reference)
		this.modalRef.content.structure = this.structure;
		this.modalRef.content.compteId = this.compteId;
		this.modalRef.content.operationStatus = StatutOperation.EnAttenteEnregistrementParAvocat;
		this.modalRef.content.editCBCompte = !this.fromBO;
		this.modalRef.content.typeCoordBancaire = 'coordonnees-bancaires';
		// Bug 19012: le compteur de PJ lit le store, la popin ne doit pas le vider en se fermant
		this.modalRef.content.resetAlfrescoDocsOnDestroy = false;

		if (this.coordonneeBancaire != null) {
			this.modalRef.content.coordonneesBancaires = this.coordonneeBancaire;
		}

		this.modalRef.content.validateCB.subscribe(cbForm => {
			if (cbForm?.value != null) {
				let compte: ICompteForUpdate = {
					compteId: this.compteId,
					coordonneeBancaireDto: cbForm?.value
				};
				this.loaderSubject.next(new StoreLoader(of(true), of(null)));

				// Bug 19012: les PJ supprimees dans la popin ne le sont reellement qu'a l'enregistrement,
				// avant l'ajout des nouvelles PJ et le rechargement du compteur
				this.compteBancaireService
					.updateCompte(compte)
					.pipe(
						switchMap(res => this.deleteCBDocuments(cbForm?.documentsToDeleteIds).pipe(map(() => res))),
						switchMap(res =>
							cbForm?.value?.documents?.length > 0
								? this.commonAlfrescoService
										.uploadCBFile({ documents: cbForm?.value?.documents, coordonneBancaireId: res.coordonneesBancaire.id })
										.pipe(map(() => res))
								: of(res)
						)
					)
					.subscribe({
						next: res => {
							this.loaderSubject.next(new StoreLoader(of(false), of(null)));
							this.store$.dispatch(
								AlfrescoDocumentStoreActions.LoadCoordonneesBancairesDocsRequest(null, res.coordonneesBancaire.id, this.compteId)
							);
							this.reloadCurrentComptes.emit();
						},
						error: error => {
							this.loaderSubject.next(new StoreLoader(of(false), of(null)));
							const config: IConfirmModalConfig = {
								canClose: false,
								textsKey: 'Preferences.UpdatePreferenceRIBError',
								confirmBtnText: 'Actions.Ok',
								modalOptions: {
									class: 'modal-md'
								}
							};

							this.confirmationService.openConfirmationModal(config, () => window.location.reload());
						}
					});
			} else if (this.coordonneeBancaire != null) {
				this.store$.dispatch(
					AlfrescoDocumentStoreActions.LoadCoordonneesBancairesDocsRequest(null, this.coordonneeBancaire.id, this.compteId)
				);
			}
		});
	}

	// Suppressions enchainees une a une : cote API chacune ajoute un evenement au compte (SaveChanges) et envoie un mail
	private deleteCBDocuments(documentIds: string[]): Observable<boolean[]> {
		return from((documentIds ?? []).filter(id => !!id)).pipe(
			concatMap(id => this.documentGedApiService.deleteDocumentsById(id, 'coordonnees-bancaires')),
			toArray()
		);
	}

	confirmDelete() {
		this.openConfirmationModal('ConfirmDeleteCB', () => this.deleteCB());
	}

	openConfirmationModal(textsKey: string, onConfirm?: () => void) {
		const config: IConfirmModalConfig = {
			canClose: true,
			canConfirm: true,
			textsKey: textsKey,
			confirmBtnText: 'Actions.Confirm',
			cancelBtnText: 'Actions.Cancel',
			modalOptions: {
				class: 'modal-md'
			}
		};
		this.confirmationService.openConfirmationModal(config, onConfirm);
	}

	deleteCB() {
		let compte: ICompteForUpdate = {
			compteId: this.compteId,
			coordonneeBancaireDto: null
		};
		this.loaderSubject.next(new StoreLoader(of(true), of(null)));
		this.compteBancaireService.updateCompte(compte).subscribe(res => {
			//TODO : delete doc
			this.reloadCurrentComptes.emit();
			this.loaderSubject.next(new StoreLoader(of(false), of(null)));
			this.store$.dispatch(AlfrescoDocumentStoreActions.resetAction());
		});
	}

	getCBHistory(){
		this.compteBancaireService.getCompteCoordonneesBancairesHistory(this.compteId).subscribe(res => {
			this.modalRef = this.modalService.show(HistoryModalComponent, {
				ignoreBackdropClick: true,
				class: 'modal-xl'
			});
			this.modalRef.content.title = 'Historique des cordonnées bancaires du cabinet';
			this.modalRef.content.selectedHistorique = res;
		});
	}

	handleOpenUploadDocumentsModal(event: MouseEvent): void {
		event.preventDefault();
		// Open modal upload document
		this.modalRef = this.modalService.show(UploadDocumentModalComponent, {
			ignoreBackdropClick: true,
			class: 'modal-xl'
		});
		this.modalRef.content.coordonneesBancairesFilesFromAlfresco = this.alfrescoDocuments;
		this.modalRef.content.isConsultDocMode = true;
		this.modalRef.content.documentType = "coordonnees-bancaires";
	}

	addTooltip() {
		return this.coordonneeBancaire != null ? this.translateService.instant('IbanExist') : '';
	}

	getDateWithoutTime(d) {
		const date = new Date(d);
		return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString()}`;
	}

	getTime(date) {
		return `${new Date(date).getHours().toString().padStart(2, '0')}h${new Date(date).getMinutes().toString().padStart(2, '0')}`;
	}

	getNomAvocat(user) {
		return `${user?.prenom} ${user?.nom?.toUpperCase()}`;
	}

	ngOnDestroy(): void {
		this.store$.dispatch(AlfrescoDocumentStoreActions.resetAction());
	}
}
